import { Draggable, Droppable } from 'react-beautiful-dnd';
import React, { MouseEvent, useEffect, useState } from 'react';
import {
  SorterCategory,
  SorterItem
} from '../../../models/LessonComponentContent';

import AssetService from '../../../../shared/services/AssetService';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Grid from '@mui/material/Unstable_Grid2';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { SorterDisplayMode } from 'content/models/LessonComponentSettings';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import styles from '../../../styles/Sorter.module.css';
import { useTheme } from '@mui/material/styles';

/**
 * Display category label (above the drop target).
 */
const DisplayLabel = (props: { label: string }) => {
  return (
    <Tooltip
      title={props.label}
      placement="bottom"
      disableHoverListener={!props.label}
      arrow
    >
      <Typography
        variant="caption"
        className={`${styles.categoryLabel} ${
          props.label && styles.categoryLabelTooltip
        }`}
        sx={{ lineHeight: 1.2 }} //Override MuiTypography caption line height
      >
        {props.label || 'Items'}
      </Typography>
    </Tooltip>
  );
};

/**
 * Display number of items in the category (below the drop target).
 */
const DisplayItemsCount = (props: { count: number }) => {
  const { count } = props;
  return (
    <Typography
      component="div"
      variant="caption"
      className={styles.horizontallyCentered}
    >
      {count === 1 ? '1 item' : `${count || 0} items`}
    </Typography>
  );
};

/**
 * Tile image, used for both draggable topmost item and non-interactive stack.
 */
const ItemImage = (props: {
  item: SorterItem;
  displayMode?: SorterDisplayMode;
}) => {
  const { item, displayMode } = props;
  const { image, label } = item;
  return (
    <>
      <div
        className={
          displayMode === 'image_only' ? '' : styles.sorterItemImageContainer
        }
      >
        {image ? (
          <img
            className={styles.sorterItemImage}
            src={AssetService.getAssetUrl(image)}
          />
        ) : (
          <div className={styles.sorterItemImageNone}></div>
        )}
      </div>
      {displayMode !== 'image_only' && (
        <Typography
          component="div"
          variant="caption"
          className={styles.sorterItemText}
        >
          {label}
        </Typography>
      )}
    </>
  );
};

/**
 * Tile label showing the category label in the center and an instruction in the
 * lower thirds.
 */
const CategoryInstruction = (props: { label: string; instruction: string }) => {
  const { label, instruction } = props;
  return (
    <div className={styles.categorySelected}>
      <Typography
        component="div"
        variant="subtitle2"
        className={styles.categoryLabelSelected}
      >
        {label}
      </Typography>
      <Typography
        component="div"
        variant="caption"
        className={styles.categoryInstruction}
      >
        {instruction}
      </Typography>
    </div>
  );
};

/**
 * Tile check mark for selected (active) tile.
 */
const ItemCheck = (props: { color: string }) => {
  return (
    <div className={styles.centered}>
      <CheckCircleIcon style={{ color: props.color }} />
    </div>
  );
};

/**
 * Label to show centered, e.g. when there are no items in the stack or an item
 * is ready to be placed.
 */
const CenterLabel = (props: { label: string }) => {
  return (
    <Typography component="div" variant="caption" className={styles.centered}>
      {props.label}
    </Typography>
  );
};

export interface SorterDroppableControlProps {
  /** Callback to change the active item in response to clicks. */
  onItemChange?: (inc: number) => void;
}

/**
 * Component to handle the prev/next controls on a droppable..
 */
const SorterDroppableControl = (props: SorterDroppableControlProps) => {
  const { onItemChange } = props;

  const onIncClick = (inc: number) => (e: MouseEvent) => {
    e.stopPropagation();
    onItemChange(inc);
  };

  return (
    <Grid
      container
      className={styles.sorterItemControls}
      justifyContent="space-between"
      alignItems="flex-end"
      wrap="nowrap"
    >
      <Grid>
        <IconButton size="small" onClick={onIncClick(-1)}>
          <KeyboardArrowLeftIcon className={styles.sorterItemArrowButton} />
        </IconButton>
      </Grid>
      <Grid>
        <IconButton size="small" onClick={onIncClick(+1)}>
          <KeyboardArrowRightIcon className={styles.sorterItemArrowButton} />
        </IconButton>
      </Grid>
    </Grid>
  );
};

/** Properties passed to a source or destination droppable. */
export interface SorterDroppableProps {
  /** Default category label */
  label: string;
  /** Category or source with items, label, etc. */
  category: SorterCategory;
  /** Display mode, e.g. image_only. */
  displayMode: SorterDisplayMode;
  /** Optional value indicating that the drag/drop should be disabled. */
  disable?: boolean;
  /** Identifier of selected item (click on source), if any. */
  activeId?: string;
  /** Identifier of item being dragged, if any. */
  dragId?: string;
  /** Optional value indicating that this is the Source category, rather than a target. */
  isSource?: boolean;
  /** Callback for when droppable is clicked. */
  onClick: (categoryId: string, itemId?: string) => void;
}

/**
 * Droppable and contained draggable(s) for the source or destination categories
 * of the Sorter interaction.
 */
export const SorterDroppable = (props: SorterDroppableProps) => {
  const {
    label,
    category,
    displayMode,
    disable,
    activeId,
    dragId,
    isSource,
    onClick
  } = props;
  const [index, setIndex] = useState(0);
  const primaryColor = useTheme().palette.primary?.main;

  const items = category.items;
  const count = items.length;
  const item = items[index];
  useEffect(() => {
    setIndex(count - 1);
  }, [count]);

  /** The area is clicked. Skip the callback in case of drag. */
  const _onClick = () => {
    !dragId && !disable && onClick(category.id, item?.id);
  };

  /** Increment or decrement the current item, with wraparound. */
  const onItemChange = (inc: number) => {
    setIndex((index + count + inc) % count);
  };

  /** CSS classes to assign to droppable container. */
  const className = [styles.sorterItemContainer]
    .concat(item ? styles.itemDropShadow : styles.noItems)
    .concat(activeId || dragId ? styles.itemDropTarget : [])
    .join(' ');

  return (
    <Grid container direction="column">
      <Grid
        className={`${styles.categoryLabelContainer} ${styles.horizontallyCentered}`}
      >
        <DisplayLabel label={label} />
      </Grid>

      <Grid>
        <Droppable droppableId={category.id} isDropDisabled={disable}>
          {(dropProvided, dropSnapshot) => (
            <div
              ref={dropProvided.innerRef}
              {...dropProvided.droppableProps}
              className={className}
              style={{
                borderColor:
                  activeId || dragId
                    ? primaryColor
                    : item && !activeId
                    ? '#e5e5e5'
                    : '#30383e'
              }}
              data-over={dropSnapshot.isDraggingOver}
              onClick={_onClick}
            >
              {isSource && !count && <CenterLabel label="No more items" />}
              {!isSource && (activeId || dragId) && (
                <CategoryInstruction
                  label={category.label}
                  instruction={activeId ? 'Select to place' : 'Drop to place'}
                />
              )}
              {!!count && (isSource || !dragId) && (isSource || !activeId) && (
                <div className={styles.sorterImageStack}>
                  {items
                    .filter((i) => i.id !== (dragId || item?.id))
                    .map((i) => (
                      <ItemImage
                        key={i.id}
                        item={i}
                        displayMode={displayMode}
                      />
                    ))}
                </div>
              )}
              {!!item && (!dragId || dragId === item.id) && (
                <Draggable
                  key={item.id}
                  draggableId={item.id}
                  index={index}
                  isDragDisabled={disable || !!activeId}
                >
                  {(dragProvided, dragSnapshot) => (
                    <div
                      key={item.id}
                      ref={dragProvided.innerRef}
                      {...dragProvided.draggableProps}
                      {...dragProvided.dragHandleProps}
                      className={styles.sorterImageDraggableContainer}
                    >
                      {(!activeId || isSource) && (
                        <ItemImage item={item} displayMode={displayMode} />
                      )}
                      {item.id === activeId && !dragId && (
                        <ItemCheck color={primaryColor} />
                      )}
                    </div>
                  )}
                </Draggable>
              )}

              {count > 1 && !activeId && !dragId && (
                <SorterDroppableControl onItemChange={onItemChange} />
              )}
              {dropProvided.placeholder}
            </div>
          )}
        </Droppable>
      </Grid>

      <Grid>
        <DisplayItemsCount count={count} />
      </Grid>
    </Grid>
  );
};
